import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import UserAvatar from '@aurora/shared-client/components/users/UserAvatar/UserAvatar';
import { RsvpResponse } from '@aurora/shared-generated/types/graphql-schema-types';
import type { OccasionTopicMessage } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import dynamic from 'next/dynamic';
import React, { useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type {
  OccasionRsvpsQuery,
  OccasionRsvpsQueryVariables,
  UserAvatarFragment
} from '../../../types/graphql-types';
import useTranslation from '../../useTranslation';
import occasionRsvpsQuery from '../OccasionRsvpIndicator/OccasionRsvps.query.graphql';
import localStyles from './OccasionAttendees.module.pcss';

const OccasionAttendeesDetail = dynamic(
  () => import('../OccasionAttendeesDetail/OccasionAttendeesDetail'),
  {
    ssr: false
  }
);

interface Props {
  /**
   * The className for the component.
   */
  className?: string;
  /**
   * The occasion for which rsvp is shown
   */
  occasion: OccasionTopicMessage;
}

/**
 * Display rsvp attendees count for a published occasion.
 * @author Vishnu Das
 */
const OccasionAttendees: React.FC<React.PropsWithChildren<Props>> = ({ className, occasion }) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.OCCASION_ATTENDEES
  );

  const { data: occasionRsvpAttendingData, loading: occasionRsvpAttendingLoading } =
    useQueryWithTracing<OccasionRsvpsQuery, OccasionRsvpsQueryVariables>(
      module,
      occasionRsvpsQuery,
      {
        variables: {
          constraints: {
            occasionId: { eq: occasion?.id },
            rsvpResponse: { eq: RsvpResponse.Yes }
          },
          first: 5
        }
      }
    );

  const rsvpAttendingCount = occasionRsvpAttendingData?.rsvps?.totalCount;
  const rsvpAttending = occasionRsvpAttendingData?.rsvps?.edges?.map(edge => edge.node);

  const { data: occasionRsvpInterestedData, loading: occasionRsvpLoading } = useQueryWithTracing<
    OccasionRsvpsQuery,
    OccasionRsvpsQueryVariables
  >(module, occasionRsvpsQuery, {
    variables: {
      constraints: {
        occasionId: { eq: occasion?.id },
        rsvpResponse: { eq: RsvpResponse.Maybe }
      },
      first: 5
    },
    skip: rsvpAttendingCount > 0
  });

  const [showAttendeeModal, setShowAttendeeModal] = useState<boolean>(false);
  if (textLoading || occasionRsvpAttendingLoading || occasionRsvpLoading) {
    return null;
  }

  const rsvpsData =
    rsvpAttendingCount > 0
      ? rsvpAttending
      : occasionRsvpInterestedData.rsvps.edges?.map(edge => edge.node);

  const firstFiveRsvps = rsvpsData.slice(0, 5);
  const totalCount =
    rsvpAttendingCount > 0 ? rsvpAttendingCount : occasionRsvpInterestedData?.rsvps?.totalCount;

  if (totalCount === 0) {
    return null;
  }

  /**
   * To render the attendee avatar list with count.
   */
  function renderAttendeeAvatars(): React.ReactElement {
    return (
      <div className={cx('lia-wrap', className)} data-testid="OccasionAttendees">
        <Button
          className={cx('lia-btn')}
          variant={ButtonVariant.UNSTYLED}
          onClick={() => {
            setShowAttendeeModal(true);
          }}
        >
          <div className={cx('d-flex lia-g-mr-10')}>
            {firstFiveRsvps.map((rsvp, index) => (
              <React.Fragment key={index}>
                <UserAvatar
                  key={rsvp?.id}
                  user={rsvp?.user as UserAvatarFragment}
                  size={IconSize.PX_24}
                  className={cx('lia-avatar')}
                ></UserAvatar>
              </React.Fragment>
            ))}
          </div>
          <div className={cx('lia-count')}>
            {rsvpAttending?.length > 0
              ? formatMessage('attendeeCount', { totalCount })
              : formatMessage('interestedCount', { totalCount })}
          </div>
        </Button>
      </div>
    );
  }

  return (
    <>
      {renderAttendeeAvatars()}
      {showAttendeeModal && (
        <OccasionAttendeesDetail
          show={showAttendeeModal}
          onHide={() => {
            setShowAttendeeModal(false);
          }}
          occasion={occasion}
        />
      )}
    </>
  );
};

export default OccasionAttendees;
